// ═══════════════════════════════════════════════════════════
// Shelter Store — Shelters, Survivors, Families, Pets, Vehicles,
// Medical Log, Transfers, Referrals
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';
import type { Shelter, Survivor, ShelterFamily, ShelterPet, ShelterVehicle, ShelterMedicalEntry, ShelterTransfer, ShelterReferral } from '../types/shelter';
import type { QueryOptions } from '../api/types';

function getIncidentFilter(): { field: string; operator: 'eq'; value: unknown }[] {
  const inc = useAuthStore.getState().activeIncident;
  return inc ? [{ field: FIELDS.shelters.incidentId, operator: 'eq', value: inc.id }] : [];
}

const FS = FIELDS.shelters;
const FSv = FIELDS.survivors;
const FFam = FIELDS.shelterFamilies;
const FPet = FIELDS.shelterPets;
const FVeh = FIELDS.shelterVehicles;
const FMed = FIELDS.shelterMedicalLog;
const FTr = FIELDS.shelterTransfers;
const FRef = FIELDS.shelterReferrals;

function mapShelter(r: Record<string, unknown>): Shelter {
  return {
    id: r.record_id as number, incidentId: (r[FS.incidentId] as number) || null,
    name: (r[FS.name] as string) || '', address: (r[FS.address] as string) || '',
    city: (r[FS.city] as string) || '', state: (r[FS.state] as string) || '', zip: (r[FS.zip] as string) || '',
    latitude: (r[FS.latitude] as number) || null, longitude: (r[FS.longitude] as number) || null,
    capacity: Number(r[FS.capacity]) || 0, currentPopulation: Number(r[FS.currentPopulation]) || 0,
    shelterType: (r[FS.shelterType] as string) || 'general', status: (r[FS.status] as string) || 'standby',
    isADA: Boolean(r[FS.isADA]), isPetFriendly: Boolean(r[FS.isPetFriendly]),
    amenities: (r[FS.amenities] as string) || '', contactName: (r[FS.contactName] as string) || '',
    contactPhone: (r[FS.contactPhone] as string) || '', operatingOrg: (r[FS.operatingOrg] as string) || '',
    notes: (r[FS.notes] as string) || '',
  };
}

function mapSurvivor(r: Record<string, unknown>): Survivor {
  return {
    id: r.record_id as number, shelterId: (r[FSv.shelterId] as number) || 0,
    incidentId: (r[FSv.incidentId] as number) || null, familyId: (r[FSv.familyId] as number) || null,
    firstName: (r[FSv.firstName] as string) || '', lastName: (r[FSv.lastName] as string) || '',
    dateOfBirth: (r[FSv.dateOfBirth] as string) || '', gender: (r[FSv.gender] as string) || '',
    phone: (r[FSv.phone] as string) || '', email: (r[FSv.email] as string) || '',
    address: (r[FSv.address] as string) || '', city: (r[FSv.city] as string) || '',
    state: (r[FSv.state] as string) || '', zip: (r[FSv.zip] as string) || '',
    emergencyContactName: (r[FSv.emergencyContactName] as string) || '',
    emergencyContactPhone: (r[FSv.emergencyContactPhone] as string) || '',
    medicalNeeds: (r[FSv.medicalNeeds] as string) || '', dietaryNeeds: (r[FSv.dietaryNeeds] as string) || '',
    hasPets: Boolean(r[FSv.hasPets]), status: (r[FSv.status] as string) || 'checked_in',
    checkInTime: (r[FSv.checkInTime] as string) || '', checkOutTime: (r[FSv.checkOutTime] as string) || '',
    registeredBy: (r[FSv.registeredBy] as string) || '', notes: (r[FSv.notes] as string) || '',
  };
}

function mapFamily(r: Record<string, unknown>): ShelterFamily {
  return {
    id: r.record_id as number,
    shelterId: Number(r[FFam.shelterId]) || 0,
    familyName: (r[FFam.familyName] as string) || '',
    headOfHousehold: (r[FFam.headOfHousehold] as string) || '',
    memberCount: Number(r[FFam.memberCount]) || 0,
    unit: (r[FFam.unit] as string) || '',
    notes: (r[FFam.notes] as string) || '',
  };
}

function mapPet(r: Record<string, unknown>): ShelterPet {
  return {
    id: r.record_id as number,
    survivorId: String(r[FPet.survivorId] || ''),
    shelterId: String(r[FPet.shelterId] || ''),
    petName: (r[FPet.petName] as string) || '',
    species: (r[FPet.species] as string) || '',
    breed: (r[FPet.breed] as string) || '',
    color: (r[FPet.color] as string) || '',
    weight: (r[FPet.weight] as string) || '',
    vaccinated: Boolean(r[FPet.vaccinated]),
    specialNeeds: (r[FPet.specialNeeds] as string) || '',
    notes: (r[FPet.notes] as string) || '',
  };
}

function mapVehicle(r: Record<string, unknown>): ShelterVehicle {
  return {
    id: r.record_id as number,
    survivorId: String(r[FVeh.survivorId] || ''),
    shelterId: String(r[FVeh.shelterId] || ''),
    make: (r[FVeh.make] as string) || '',
    model: (r[FVeh.model] as string) || '',
    year: (r[FVeh.year] as string) || '',
    color: (r[FVeh.color] as string) || '',
    licensePlate: (r[FVeh.licensePlate] as string) || '',
    parkingLocation: (r[FVeh.parkingLocation] as string) || '',
    notes: (r[FVeh.notes] as string) || '',
  };
}

function mapMedical(r: Record<string, unknown>): ShelterMedicalEntry {
  return {
    id: r.record_id as number,
    survivorId: String(r[FMed.survivorId] || ''),
    shelterId: String(r[FMed.shelterId] || ''),
    incidentType: (r[FMed.incidentType] as string) || '',
    description: (r[FMed.description] as string) || '',
    treatment: (r[FMed.treatment] as string) || '',
    severity: (r[FMed.severity] as string) || '',
    treatedBy: (r[FMed.treatedBy] as string) || '',
    referral: (r[FMed.referral] as string) || '',
    timestamp: (r[FMed.timestamp] as string) || '',
    notes: (r[FMed.notes] as string) || '',
  };
}

function mapTransfer(r: Record<string, unknown>): ShelterTransfer {
  return {
    id: r.record_id as number,
    survivorId: String(r[FTr.survivorId] || ''),
    fromShelterId: String(r[FTr.fromShelterId] || ''),
    toShelterId: String(r[FTr.toShelterId] || ''),
    reason: (r[FTr.reason] as string) || '',
    transferDate: (r[FTr.transferDate] as string) || '',
    authorizedBy: (r[FTr.authorizedBy] as string) || '',
    notes: (r[FTr.notes] as string) || '',
  };
}

function mapReferral(r: Record<string, unknown>): ShelterReferral {
  return {
    id: r.record_id as number,
    survivorId: String(r[FRef.survivorId] || ''),
    shelterId: String(r[FRef.shelterId] || ''),
    referralType: (r[FRef.referralType] as string) || '',
    agency: (r[FRef.agency] as string) || '',
    contactInfo: (r[FRef.contactInfo] as string) || '',
    status: (r[FRef.status] as string) || 'pending',
    notes: (r[FRef.notes] as string) || '',
  };
}

function petToPayload(data: Partial<ShelterPet>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.survivorId !== undefined) p[FPet.survivorId] = data.survivorId;
  if (data.shelterId !== undefined) p[FPet.shelterId] = data.shelterId;
  if (data.petName !== undefined) p[FPet.petName] = data.petName;
  if (data.species !== undefined) p[FPet.species] = data.species;
  if (data.breed !== undefined) p[FPet.breed] = data.breed;
  if (data.color !== undefined) p[FPet.color] = data.color;
  if (data.weight !== undefined) p[FPet.weight] = data.weight;
  if (data.vaccinated !== undefined) p[FPet.vaccinated] = data.vaccinated;
  if (data.specialNeeds !== undefined) p[FPet.specialNeeds] = data.specialNeeds;
  if (data.notes !== undefined) p[FPet.notes] = data.notes;
  return p;
}

function vehicleToPayload(data: Partial<ShelterVehicle>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.survivorId !== undefined) p[FVeh.survivorId] = data.survivorId;
  if (data.shelterId !== undefined) p[FVeh.shelterId] = data.shelterId;
  if (data.make !== undefined) p[FVeh.make] = data.make;
  if (data.model !== undefined) p[FVeh.model] = data.model;
  if (data.year !== undefined) p[FVeh.year] = data.year;
  if (data.color !== undefined) p[FVeh.color] = data.color;
  if (data.licensePlate !== undefined) p[FVeh.licensePlate] = data.licensePlate;
  if (data.parkingLocation !== undefined) p[FVeh.parkingLocation] = data.parkingLocation;
  if (data.notes !== undefined) p[FVeh.notes] = data.notes;
  return p;
}

function familyToPayload(data: Partial<ShelterFamily>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.shelterId !== undefined) p[FFam.shelterId] = data.shelterId;
  if (data.familyName !== undefined) p[FFam.familyName] = data.familyName;
  if (data.headOfHousehold !== undefined) p[FFam.headOfHousehold] = data.headOfHousehold;
  if (data.memberCount !== undefined) p[FFam.memberCount] = data.memberCount;
  if (data.unit !== undefined) p[FFam.unit] = data.unit;
  if (data.notes !== undefined) p[FFam.notes] = data.notes;
  return p;
}

function shelterToPayload(data: Partial<Shelter>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.name !== undefined) p[FS.name] = data.name;
  if (data.address !== undefined) p[FS.address] = data.address;
  if (data.city !== undefined) p[FS.city] = data.city;
  if (data.state !== undefined) p[FS.state] = data.state;
  if (data.zip !== undefined) p[FS.zip] = data.zip;
  if (data.latitude !== undefined) p[FS.latitude] = data.latitude;
  if (data.longitude !== undefined) p[FS.longitude] = data.longitude;
  if (data.capacity !== undefined) p[FS.capacity] = data.capacity;
  if (data.currentPopulation !== undefined) p[FS.currentPopulation] = data.currentPopulation;
  if (data.shelterType !== undefined) p[FS.shelterType] = data.shelterType;
  if (data.status !== undefined) p[FS.status] = data.status;
  if (data.isADA !== undefined) p[FS.isADA] = data.isADA;
  if (data.isPetFriendly !== undefined) p[FS.isPetFriendly] = data.isPetFriendly;
  if (data.contactName !== undefined) p[FS.contactName] = data.contactName;
  if (data.contactPhone !== undefined) p[FS.contactPhone] = data.contactPhone;
  if (data.operatingOrg !== undefined) p[FS.operatingOrg] = data.operatingOrg;
  if (data.incidentId !== undefined) p[FS.incidentId] = data.incidentId;
  if (data.notes !== undefined) p[FS.notes] = data.notes;
  return p;
}

function survivorToPayload(data: Partial<Survivor>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.shelterId !== undefined) p[FSv.shelterId] = data.shelterId;
  if (data.incidentId !== undefined) p[FSv.incidentId] = data.incidentId;
  if (data.familyId !== undefined) p[FSv.familyId] = data.familyId;
  if (data.firstName !== undefined) p[FSv.firstName] = data.firstName;
  if (data.lastName !== undefined) p[FSv.lastName] = data.lastName;
  if (data.dateOfBirth !== undefined) p[FSv.dateOfBirth] = data.dateOfBirth;
  if (data.gender !== undefined) p[FSv.gender] = data.gender;
  if (data.phone !== undefined) p[FSv.phone] = data.phone;
  if (data.email !== undefined) p[FSv.email] = data.email;
  if (data.address !== undefined) p[FSv.address] = data.address;
  if (data.city !== undefined) p[FSv.city] = data.city;
  if (data.state !== undefined) p[FSv.state] = data.state;
  if (data.zip !== undefined) p[FSv.zip] = data.zip;
  if (data.emergencyContactName !== undefined) p[FSv.emergencyContactName] = data.emergencyContactName;
  if (data.emergencyContactPhone !== undefined) p[FSv.emergencyContactPhone] = data.emergencyContactPhone;
  if (data.medicalNeeds !== undefined) p[FSv.medicalNeeds] = data.medicalNeeds;
  if (data.dietaryNeeds !== undefined) p[FSv.dietaryNeeds] = data.dietaryNeeds;
  if (data.hasPets !== undefined) p[FSv.hasPets] = data.hasPets;
  if (data.status !== undefined) p[FSv.status] = data.status;
  if (data.checkInTime !== undefined) p[FSv.checkInTime] = data.checkInTime;
  if (data.checkOutTime !== undefined) p[FSv.checkOutTime] = data.checkOutTime;
  if (data.registeredBy !== undefined) p[FSv.registeredBy] = data.registeredBy;
  if (data.notes !== undefined) p[FSv.notes] = data.notes;
  return p;
}

interface ShelterState {
  // Shelters
  shelters: Shelter[];
  sheltersLoading: boolean;
  fetchShelters: () => Promise<void>;

  // Survivors (paginated)
  survivors: Survivor[];
  survivorsTotal: number;
  survivorsLoading: boolean;
  survivorsPage: number;
  fetchSurvivors: (opts?: Partial<QueryOptions>) => Promise<void>;
  setSurvivorsPage: (page: number) => void;

  // Related data
  families: ShelterFamily[];
  pets: ShelterPet[];
  vehicles: ShelterVehicle[];
  medicalLog: ShelterMedicalEntry[];
  transfers: ShelterTransfer[];
  referrals: ShelterReferral[];
  fetchFamilies: () => Promise<void>;
  fetchPets: () => Promise<void>;
  fetchVehicles: () => Promise<void>;
  fetchMedicalLog: () => Promise<void>;
  fetchTransfers: () => Promise<void>;
  fetchReferrals: () => Promise<void>;

  // CRUD — Shelters
  createShelter: (data: Record<string, unknown>) => Promise<number | null>;
  updateShelter: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteShelter: (id: number) => Promise<void>;

  // CRUD — Survivors
  createSurvivor: (data: Record<string, unknown>) => Promise<number | null>;
  updateSurvivor: (id: number, data: Partial<Survivor>) => Promise<void>;
  checkOutSurvivor: (id: number) => Promise<void>;

  // CRUD — Pets
  createPet: (data: Partial<ShelterPet>) => Promise<number | null>;
  updatePet: (id: number, data: Partial<ShelterPet>) => Promise<void>;
  deletePet: (id: number) => Promise<void>;

  // CRUD — Vehicles
  createVehicle: (data: Partial<ShelterVehicle>) => Promise<number | null>;
  updateVehicle: (id: number, data: Partial<ShelterVehicle>) => Promise<void>;
  deleteVehicle: (id: number) => Promise<void>;

  // CRUD — Families
  createFamily: (data: Partial<ShelterFamily>) => Promise<number | null>;
  updateFamily: (id: number, data: Partial<ShelterFamily>) => Promise<void>;
  deleteFamily: (id: number) => Promise<void>;

  // CRUD — Other
  createMedicalEntry: (data: Partial<ShelterMedicalEntry>) => Promise<number | null>;
  createTransfer: (data: Partial<ShelterTransfer>) => Promise<number | null>;
  createReferral: (data: Partial<ShelterReferral>) => Promise<number | null>;

  // V1 compat aliases
  loadShelters: () => Promise<void>;
  loadSurvivors: () => Promise<void>;
  loadFamilies: () => Promise<void>;
  loadPets: () => Promise<void>;
  loadVehicles: () => Promise<void>;
  loadMedicalLog: () => Promise<void>;
  loadTransfers: () => Promise<void>;
  loadReferrals: () => Promise<void>;
  loadingShelters: boolean;
  loadingSurvivors: boolean;
  loadingFamilies: boolean;
  isLoading: boolean;
}

export const useShelterStore = create<ShelterState>((set, get) => ({
  shelters: [], sheltersLoading: false,
  survivors: [], survivorsTotal: 0, survivorsLoading: false, survivorsPage: 1,
  families: [], pets: [], vehicles: [], medicalLog: [], transfers: [], referrals: [],

  fetchShelters: async () => {
    set({ sheltersLoading: true });
    try {
      const incFilter = getIncidentFilter();
      const res = incFilter.length > 0
        ? await queryRecords<Record<string, unknown>>(TABLES.shelters, { limit: 200, filters: incFilter })
        : await fetchRecords<Record<string, unknown>>(TABLES.shelters, { limit: 200 });
      set({ shelters: res.records.map(mapShelter) });
    } catch (e) { console.error('[Shelter] fetchShelters', e); }
    set({ sheltersLoading: false });
  },

  fetchSurvivors: async (opts) => {
    set({ survivorsLoading: true });
    try {
      const { survivorsPage } = get();
      const incFilter = getIncidentFilter().map(f => ({ ...f, field: FIELDS.survivors.incidentId }));
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.survivors, {
        limit: 50, offset: (survivorsPage - 1) * 50,
        sort: { field: 'created_at', direction: 'desc' },
        ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ survivors: res.records.map(mapSurvivor), survivorsTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchSurvivors', e); }
    set({ survivorsLoading: false });
  },

  setSurvivorsPage: (page) => { set({ survivorsPage: page }); get().fetchSurvivors(); },

  fetchFamilies: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.shelterFamilies, { limit: 500 });
      set({ families: res.records.map(mapFamily) });
    } catch (e) { console.error('[Shelter] fetchFamilies', e); }
  },

  fetchPets: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.shelterPets, { limit: 500 });
      set({ pets: res.records.map(mapPet) });
    } catch (e) { console.error('[Shelter] fetchPets', e); }
  },

  fetchVehicles: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.shelterVehicles, { limit: 500 });
      set({ vehicles: res.records.map(mapVehicle) });
    } catch (e) { console.error('[Shelter] fetchVehicles', e); }
  },

  fetchMedicalLog: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.shelterMedicalLog, { limit: 1000 });
      set({ medicalLog: res.records.map(mapMedical) });
    } catch (e) { console.error('[Shelter] fetchMedicalLog', e); }
  },

  fetchTransfers: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.shelterTransfers, { limit: 500 });
      set({ transfers: res.records.map(mapTransfer) });
    } catch (e) { console.error('[Shelter] fetchTransfers', e); }
  },

  fetchReferrals: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.shelterReferrals, { limit: 500 });
      set({ referrals: res.records.map(mapReferral) });
    } catch (e) { console.error('[Shelter] fetchReferrals', e); }
  },

  createShelter: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelters, shelterToPayload(data as Partial<Shelter>));
      await get().fetchShelters();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createShelter', e); return null; }
  },
  updateShelter: async (id, data) => { try { await updateRecord(TABLES.shelters, id, shelterToPayload(data as Partial<Shelter>)); await get().fetchShelters(); } catch (e) { console.error('[Shelter]', e); } },
  deleteShelter: async (id) => { try { await deleteRecord(TABLES.shelters, id); await get().fetchShelters(); } catch (e) { console.error('[Shelter]', e); } },

  createSurvivor: async (data) => {
    try {
      const payload = survivorToPayload(data as Partial<Survivor>);
      payload[FSv.checkInTime] = new Date().toISOString();
      if (!payload[FSv.status]) payload[FSv.status] = 'checked_in';
      const res = await createRecord<Record<string, unknown>>(TABLES.survivors, payload);
      await get().fetchSurvivors();
      await get().fetchShelters();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createSurvivor', e); return null; }
  },

  updateSurvivor: async (id, data) => {
    try {
      await updateRecord(TABLES.survivors, id, survivorToPayload(data));
      await get().fetchSurvivors();
    } catch (e) { console.error('[Shelter] updateSurvivor', e); }
  },

  checkOutSurvivor: async (id) => {
    try {
      await updateRecord(TABLES.survivors, id, { [FSv.status]: 'checked_out', [FSv.checkOutTime]: new Date().toISOString() });
      await get().fetchSurvivors();
      await get().fetchShelters();
    } catch (e) { console.error('[Shelter] checkOut', e); }
  },

  // Pet CRUD
  createPet: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterPets, petToPayload(data));
      await get().fetchPets();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createPet', e); return null; }
  },
  updatePet: async (id, data) => {
    try { await updateRecord(TABLES.shelterPets, id, petToPayload(data)); await get().fetchPets(); }
    catch (e) { console.error('[Shelter] updatePet', e); }
  },
  deletePet: async (id) => {
    try { await deleteRecord(TABLES.shelterPets, id); await get().fetchPets(); }
    catch (e) { console.error('[Shelter] deletePet', e); }
  },

  // Vehicle CRUD
  createVehicle: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterVehicles, vehicleToPayload(data));
      await get().fetchVehicles();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createVehicle', e); return null; }
  },
  updateVehicle: async (id, data) => {
    try { await updateRecord(TABLES.shelterVehicles, id, vehicleToPayload(data)); await get().fetchVehicles(); }
    catch (e) { console.error('[Shelter] updateVehicle', e); }
  },
  deleteVehicle: async (id) => {
    try { await deleteRecord(TABLES.shelterVehicles, id); await get().fetchVehicles(); }
    catch (e) { console.error('[Shelter] deleteVehicle', e); }
  },

  // Family CRUD
  createFamily: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterFamilies, familyToPayload(data));
      await get().fetchFamilies();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createFamily', e); return null; }
  },
  updateFamily: async (id, data) => {
    try { await updateRecord(TABLES.shelterFamilies, id, familyToPayload(data)); await get().fetchFamilies(); }
    catch (e) { console.error('[Shelter] updateFamily', e); }
  },
  deleteFamily: async (id) => {
    try { await deleteRecord(TABLES.shelterFamilies, id); await get().fetchFamilies(); }
    catch (e) { console.error('[Shelter] deleteFamily', e); }
  },

  createMedicalEntry: async (data) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.survivorId) p[FMed.survivorId] = data.survivorId;
      if (data.shelterId) p[FMed.shelterId] = data.shelterId;
      if (data.incidentType) p[FMed.incidentType] = data.incidentType;
      if (data.description) p[FMed.description] = data.description;
      if (data.treatment) p[FMed.treatment] = data.treatment;
      if (data.severity) p[FMed.severity] = data.severity;
      if (data.treatedBy) p[FMed.treatedBy] = data.treatedBy;
      if (data.referral) p[FMed.referral] = data.referral;
      p[FMed.timestamp] = data.timestamp || new Date().toISOString();
      if (data.notes) p[FMed.notes] = data.notes;
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterMedicalLog, p);
      await get().fetchMedicalLog();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createMedicalEntry', e); return null; }
  },

  createTransfer: async (data) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.survivorId) p[FTr.survivorId] = data.survivorId;
      if (data.fromShelterId) p[FTr.fromShelterId] = data.fromShelterId;
      if (data.toShelterId) p[FTr.toShelterId] = data.toShelterId;
      if (data.reason) p[FTr.reason] = data.reason;
      p[FTr.transferDate] = data.transferDate || new Date().toISOString();
      if (data.authorizedBy) p[FTr.authorizedBy] = data.authorizedBy;
      if (data.notes) p[FTr.notes] = data.notes;
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterTransfers, p);
      await get().fetchTransfers();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createTransfer', e); return null; }
  },

  createReferral: async (data) => {
    try {
      const p: Record<string, unknown> = {};
      if (data.survivorId) p[FRef.survivorId] = data.survivorId;
      if (data.shelterId) p[FRef.shelterId] = data.shelterId;
      if (data.referralType) p[FRef.referralType] = data.referralType;
      if (data.agency) p[FRef.agency] = data.agency;
      if (data.contactInfo) p[FRef.contactInfo] = data.contactInfo;
      if (data.status) p[FRef.status] = data.status;
      if (data.notes) p[FRef.notes] = data.notes;
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterReferrals, p);
      await get().fetchReferrals();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createReferral', e); return null; }
  },

  // V1 compat aliases
  loadShelters: async () => get().fetchShelters(),
  loadSurvivors: async () => get().fetchSurvivors(),
  loadFamilies: async () => get().fetchFamilies(),
  loadPets: async () => get().fetchPets(),
  loadVehicles: async () => get().fetchVehicles(),
  loadMedicalLog: async () => get().fetchMedicalLog(),
  loadTransfers: async () => get().fetchTransfers(),
  loadReferrals: async () => get().fetchReferrals(),
  loadingShelters: false,
  loadingSurvivors: false,
  loadingFamilies: false,
  isLoading: false,
  shelterIncidents: [] as any[],
  loadShelterIncidents: async () => {},
  loadingIncidents: false,
  createShelterIncident: async (_data: any) => null,
  updateShelterIncident: async (_id: any, _data: any) => {},
  triageQuestions: [] as any[],
  loadTriageQuestions: async () => {},
  loadingTriageQ: false,
  createTriageQuestion: async (_data: any) => null,
  updateTriageQuestion: async (_id: any, _data: any) => {},
  deleteTriageQuestion: async (_id: any) => {},
  triageAnswers: [] as any[],
  loadTriageAnswers: async (_survivorId: any) => {},
  createTriageAnswer: async (_data: any) => null,
  supplies: [] as any[],
  loadSupplies: async () => {},
  loadingSupplies: false,
  createSupplyItem: async (_data: any) => null,
  updateSupplyItem: async (_id: any, _data: any) => {},
  deleteSupplyItem: async (_id: any) => {},
  property: [] as any[],
  loadProperty: async () => {},
  loadingProperty: false,
  createProperty: async (_data: any) => null,
  updateProperty: async (_id: any, _data: any) => {},
  deleteProperty: async (_id: any) => {},
  reunification: [] as any[],
  loadReunification: async () => {},
  loadingReunification: false,
  createReunification: async (_data: any) => null,
  updateReunification: async (_id: any, _data: any) => {},
  loadingMedical: false,
  updateMedicalEntry: async (_id: any, _data: any) => {},
  loadingPets: false,
  loadingReferrals: false,
  updateReferral: async (_id: any, _data: any) => {},
  loadingTransfers: false,
  updateTransfer: async (_id: any, _data: any) => {},
  loadingVehicles: false,
}));
